// Pure promotion-form domain logic, extracted from PromotionEditPage so the
// validation + API-payload assembly (the risk surface) lives in one testable
// place with no React/Framework7 dependency. The page owns state + the toast/
// tab side-effects; this module owns "draft in → validated payload out".
import { DateTime } from 'luxon';
import { inputToMinor, moneyToInput } from '@/utils/money';
import {
  emptySelection,
  selectionFromTargets,
  targetsFromSelection,
  type TargetCategory,
  type TargetSelection,
} from '@/features/promotions/utils/promotion-targets';
import type {
  CreatePromotionBody,
  Promotion,
  PromotionPaymentMethodKind,
} from '@restaurant/api-client';

export type DiscountType = 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_DELIVERY';
export type TargetType = Promotion['targetType'];
export type Trigger = 'AUTOMATIC' | 'CODE_REQUIRED';
export type CouponKind = 'PUBLIC' | 'PRIVATE';
export type PromoOrderType = 'DINE_IN' | 'PICKUP' | 'DELIVERY';

type WeeklyWindow = NonNullable<CreatePromotionBody['weeklySchedule']>[number];

/** The full editable form state, flattened. Mirrors PromotionEditPage's fields. */
export interface PromotionDraft {
  nameEn: string;
  descEn: string;
  discountType: DiscountType;
  percentValue: string;
  amountValue: string;
  maxDiscount: string;
  targetType: TargetType;
  targetSelection: TargetSelection;
  orderTypes: PromoOrderType[];
  paymentMethodKinds: PromotionPaymentMethodKind[];
  minSubtotal: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  weeklySchedule: WeeklyWindow[];
  stackable: boolean;
  priority: string;
  customerVisible: boolean;
  trigger: Trigger;
  couponKind: CouponKind;
  code: string;
  maxRedemptions: string;
  oncePerCustomer: boolean;
  currency: string;
}

// Online ordering is pickup + delivery only - dine-in lives in table-ordering.
const ALL_ORDER_TYPES: PromoOrderType[] = ['PICKUP', 'DELIVERY'];

/** A stored UTC instant → the "YYYY-MM-DD" date input value, read in the
 *  restaurant timezone (so a window that ends "today" locally shows today). */
function isoToDateInput(iso: string | null, tz: string): string {
  return iso ? DateTime.fromISO(iso, { zone: 'utc' }).setZone(tz).toFormat('yyyy-MM-dd') : '';
}

/** A stored UTC instant → the "HH:mm" time input value in the restaurant tz. */
function isoToTimeInput(iso: string | null, tz: string): string {
  return iso ? DateTime.fromISO(iso, { zone: 'utc' }).setZone(tz).toFormat('HH:mm') : '';
}

/** The pristine draft for a new promotion: every field at its create-form
 *  default (mirrors PromotionEditPage's original initial useState values). */
export function emptyDraft(currency: string): PromotionDraft {
  return {
    nameEn: '',
    descEn: '',
    discountType: 'PERCENTAGE',
    percentValue: '10',
    amountValue: '',
    maxDiscount: '',
    targetType: 'ENTIRE_ORDER',
    targetSelection: emptySelection(),
    orderTypes: ['PICKUP', 'DELIVERY'],
    paymentMethodKinds: [],
    minSubtotal: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    weeklySchedule: [],
    stackable: false,
    priority: '0',
    customerVisible: true,
    trigger: 'AUTOMATIC',
    couponKind: 'PUBLIC',
    code: '',
    maxRedemptions: '',
    oncePerCustomer: true,
    currency,
  };
}

/**
 * Map an existing promotion onto a draft — the verbatim hydration the edit page
 * previously ran field-by-field in a useEffect, now a pure function. Starts from
 * `emptyDraft` and applies exactly the same conditional overrides, so fields the
 * server leaves unset keep their create-form defaults (e.g. percentValue stays
 * '10' for a non-percentage promo).
 */
export function hydrateDraft(existing: Promotion, currency: string, tz: string): PromotionDraft {
  const d = emptyDraft(currency);
  d.nameEn = existing.name.en ?? '';
  d.descEn = existing.description?.en ?? '';
  d.discountType = existing.discountType;
  if (existing.discountType === 'PERCENTAGE') d.percentValue = String(existing.discountValue / 100);
  if (existing.discountType === 'FIXED_AMOUNT')
    d.amountValue = moneyToInput(existing.discountValue, currency);
  d.maxDiscount =
    existing.maxDiscountCents != null ? moneyToInput(existing.maxDiscountCents, currency) : '';
  d.targetType = existing.targetType;
  d.targetSelection = selectionFromTargets(existing.targets);
  // Drop any order types we no longer expose online (e.g. a legacy DINE_IN).
  d.orderTypes = existing.orderTypes.filter((ot) => ALL_ORDER_TYPES.includes(ot));
  d.paymentMethodKinds = existing.paymentMethodKinds ?? [];
  d.minSubtotal =
    existing.minSubtotalCents > 0 ? moneyToInput(existing.minSubtotalCents, currency) : '';
  d.startDate = isoToDateInput(existing.startsAt, tz);
  d.startTime = isoToTimeInput(existing.startsAt, tz);
  d.endDate = isoToDateInput(existing.endsAt, tz);
  d.endTime = isoToTimeInput(existing.endsAt, tz);
  d.weeklySchedule = existing.weeklySchedule ?? [];
  d.stackable = existing.stackable;
  d.priority = String(existing.priority);
  d.customerVisible = existing.customerVisible;
  d.trigger = existing.trigger;
  if (existing.couponKind) d.couponKind = existing.couponKind;
  if (existing.code) {
    d.code = existing.code.code;
    d.maxRedemptions =
      existing.code.maxRedemptions != null ? String(existing.code.maxRedemptions) : '';
    d.oncePerCustomer = existing.code.oncePerCustomer;
  }
  return d;
}

/**
 * Combine a "YYYY-MM-DD" date + optional "HH:mm" time (restaurant-local) → UTC
 * ISO. No date → null (no bound). No time → the day edge: start of day for the
 * `start` window, end of day (23:59:59.999) for the `end` window so it stays
 * active through the whole chosen day.
 */
export function dateTimeToIso(
  date: string,
  time: string,
  tz: string,
  edge: 'start' | 'end',
): string | null {
  if (!date) return null;
  if (!time) {
    const d = DateTime.fromISO(date, { zone: tz });
    return (edge === 'end' ? d.endOf('day') : d.startOf('day')).toUTC().toISO();
  }
  return DateTime.fromISO(`${date}T${time}`, { zone: tz }).toUTC().toISO();
}

/** Which field failed validation — the page maps this to a tab + toast. */
export type PromotionBuildError = 'name' | 'orderType' | 'target' | 'dateRange' | 'schedule';

export type PromotionBuildResult =
  | { ok: true; body: CreatePromotionBody }
  | { ok: false; field: PromotionBuildError };

/**
 * Validate the draft and assemble the create/update payload. Pure — returns the
 * body on success or the first failing field on error (no side effects). The
 * validation order and every mapped field are identical to the previous inline
 * PromotionEditPage.buildBody().
 */
export function buildPromotionBody(
  d: PromotionDraft,
  tz: string,
  catalog: TargetCategory[],
): PromotionBuildResult {
  if (d.nameEn.trim().length === 0) return { ok: false, field: 'name' };
  if (d.orderTypes.length === 0) return { ok: false, field: 'orderType' };
  const targets =
    d.targetType === 'SELECTED_ITEMS' ? targetsFromSelection(d.targetSelection, catalog) : [];
  if (d.targetType === 'SELECTED_ITEMS' && targets.length === 0) {
    return { ok: false, field: 'target' };
  }
  const startIso = dateTimeToIso(d.startDate, d.startTime, tz, 'start');
  const endIso = dateTimeToIso(d.endDate, d.endTime, tz, 'end');
  if (startIso && endIso && startIso > endIso) return { ok: false, field: 'dateRange' };
  if (d.weeklySchedule.some((w) => !w.start || !w.end || w.start >= w.end)) {
    return { ok: false, field: 'schedule' };
  }

  const discountValue =
    d.discountType === 'PERCENTAGE'
      ? Math.round(parseFloat(d.percentValue || '0') * 100)
      : d.discountType === 'FIXED_AMOUNT'
        ? inputToMinor(d.amountValue, d.currency)
        : 0;
  return {
    ok: true,
    body: {
      name: { en: d.nameEn.trim() },
      description: d.descEn.trim() ? { en: d.descEn.trim() } : null,
      discountType: d.discountType,
      discountValue,
      maxDiscountCents:
        d.discountType === 'PERCENTAGE' && d.maxDiscount.trim()
          ? inputToMinor(d.maxDiscount, d.currency)
          : null,
      targetType: d.targetType,
      targets,
      minSubtotalCents: d.minSubtotal.trim() ? inputToMinor(d.minSubtotal, d.currency) : 0,
      orderTypes: d.orderTypes,
      paymentMethodKinds: d.paymentMethodKinds,
      startsAt: startIso,
      endsAt: endIso,
      weeklySchedule: d.weeklySchedule.length > 0 ? d.weeklySchedule : null,
      stackable: d.stackable,
      priority: parseInt(d.priority || '0', 10),
      trigger: d.trigger,
      couponKind: d.trigger === 'CODE_REQUIRED' ? d.couponKind : null,
      customerVisible: d.customerVisible,
      code:
        d.trigger === 'CODE_REQUIRED' && d.couponKind === 'PUBLIC' && d.code.trim()
          ? {
              code: d.code.trim(),
              maxRedemptions: d.maxRedemptions.trim() ? parseInt(d.maxRedemptions, 10) : null,
              oncePerCustomer: d.oncePerCustomer,
            }
          : null,
    },
  };
}
